/* Turning a tracker into a chart.
 *
 * A view is a small object rather than code: what to group by, what to measure,
 * how to draw it. That is what lets a view be saved against a contract and
 * simply be there every month, and what lets the monthly report ask for one by
 * name rather than by rebuilding it.
 *
 * Everything here is pure. Rows in, drawable numbers out.
 */

const num = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[$,\s]/g, "").replace(/%$/, ""));
  return Number.isFinite(n) ? n : null;
};

export const AGGREGATIONS = [
  { key: "count", label: "Count of rows", needsColumn: false },
  { key: "sum", label: "Sum", needsColumn: true },
  { key: "avg", label: "Average", needsColumn: true },
  { key: "min", label: "Lowest", needsColumn: true },
  { key: "max", label: "Highest", needsColumn: true },
];

export const CHART_KINDS = [
  { key: "bar", label: "Bars", note: "Comparing categories. The safest default." },
  { key: "column", label: "Columns", note: "Same as bars, standing up. Good for months." },
  { key: "line", label: "Line", note: "A measure over time. Needs a date to group by." },
  { key: "donut", label: "Donut", note: "Shares of one whole. Only when there are few slices." },
  { key: "kpi", label: "Number", note: "One figure, large." },
  { key: "table", label: "Table", note: "The numbers themselves." },
];

/* What a column can hold.
 *
 * Import infers text, number or date from the values. A column typed by hand
 * gets a fourth: a short list of choices, which is the difference between a
 * status somebody retypes six ways and a status you can chart.
 */
export const COLUMN_TYPES = [
  { key: "text", label: "Text", note: "Names, references, notes. Anything typed." },
  { key: "choice", label: "One of a few choices",
    note: "Open, In review, Closed. Picked from a list, drawn as a chip, easy to chart." },
  { key: "number", label: "Number", note: "Anything worth adding up or averaging." },
  { key: "date", label: "Date", note: "Charts on a timeline and groups by month." },
];

/* Choice groups like text everywhere: it is a label, not a measure. */
export const isCategory = (c) => c?.type === "text" || c?.type === "choice";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* Dates group by month unless told otherwise. Nobody wants a bar per day
   across two years, and every trend in these workbooks is monthly. */
export function bucketDate(value, bucket = "month") {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
  if (!m) return null;
  const [, y, mo, d] = m;
  if (bucket === "day") return { key: `${y}-${mo}-${d}`, label: `${mo}/${d}/${y}` };
  if (bucket === "year") return { key: y, label: y };
  if (bucket === "quarter") {
    const q = Math.floor((Number(mo) - 1) / 3) + 1;
    return { key: `${y}-Q${q}`, label: `Q${q} ${y}` };
  }
  return { key: `${y}-${mo}`, label: `${MONTHS[Number(mo) - 1]} ${y.slice(2)}` };
}

const groupValue = (row, column, bucket) => {
  const raw = row?.[column?.key];
  if (raw === null || raw === undefined || raw === "") {
    return { key: "__blank__", label: "Not set" };
  }
  if (column?.type === "date") {
    return bucketDate(raw, bucket) ?? { key: "__blank__", label: "Not set" };
  }
  return { key: String(raw), label: String(raw) };
};

function aggregate(values, agg) {
  if (agg === "count") return values.length;
  const nums = values.map(num).filter((n) => n !== null);
  if (!nums.length) return 0;
  if (agg === "sum") return nums.reduce((s, n) => s + n, 0);
  if (agg === "avg") return Math.round((nums.reduce((s, n) => s + n, 0) / nums.length) * 100) / 100;
  if (agg === "min") return Math.min(...nums);
  if (agg === "max") return Math.max(...nums);
  return nums.length;
}

const byKey = (columns, key) => columns.find((c) => c.key === key) ?? null;

/* The whole view, ready to draw.
 *
 * Returns rows shaped for a chart library plus the series names, so the
 * drawing code holds no logic about what anything means. */
export function buildView(spec = {}, columns = [], rows = []) {
  const kind = spec.kind ?? "bar";
  const measure = spec.measure ?? { agg: "count" };
  const measureCol = byKey(columns, measure.column);
  const measureLabel = measure.agg === "count"
    ? "Rows"
    : `${AGGREGATIONS.find((a) => a.key === measure.agg)?.label ?? "Sum"} of ${measureCol?.label ?? ""}`.trim();

  if (kind === "kpi") {
    const value = aggregate(
      rows.map((r) => (measureCol ? r[measureCol.key] : 1)), measure.agg);
    return { kind, value, measureLabel, rowCount: rows.length, series: [], data: [],
             rows };
  }

  const groupCol = byKey(columns, spec.groupBy);
  if (!groupCol) {
    return { kind, data: [], series: [], measureLabel, error: "Pick something to group by." };
  }
  const splitCol = byKey(columns, spec.splitBy);

  /* group key -> { label, series name -> raw values, series name -> rows }
   *
   * The rows are carried alongside the numbers, not recomputed later from the
   * spec. A bar that cannot produce the records it was drawn from is a number
   * nobody can act on, and reconstructing them afterwards would mean writing
   * the grouping rules twice and having them disagree. */
  const groups = new Map();
  const seriesSeen = new Set();

  for (const row of rows) {
    const g = groupValue(row, groupCol, spec.bucket);
    if (!groups.has(g.key)) {
      groups.set(g.key, { key: g.key, label: g.label, buckets: new Map(), rowsBy: new Map() });
    }
    const entry = groups.get(g.key);

    const seriesName = splitCol
      ? String(row[splitCol.key] ?? "Not set")
      : measureLabel;
    seriesSeen.add(seriesName);

    if (!entry.buckets.has(seriesName)) {
      entry.buckets.set(seriesName, []);
      entry.rowsBy.set(seriesName, []);
    }
    entry.buckets.get(seriesName).push(measureCol ? row[measureCol.key] : 1);
    entry.rowsBy.get(seriesName).push(row);
  }

  /* A split into more series than anyone can tell apart is not a chart, it is
     confetti with a legend. Beyond the cap the rest are gathered into one
     "Other" band, and the caller is told, so the screen can say so rather than
     quietly dropping data. */
  const MAX_SERIES = 12;
  let series = [...seriesSeen].sort();
  let seriesCapped = 0;

  if (splitCol && series.length > MAX_SERIES) {
    const totals = new Map(series.map((name) => [name, 0]));
    for (const g of groups.values()) {
      for (const [name, vals] of g.buckets) {
        totals.set(name, totals.get(name) + aggregate(vals, measure.agg));
      }
    }
    const keep = new Set([...series]
      .sort((a, b) => totals.get(b) - totals.get(a))
      .slice(0, MAX_SERIES - 1));
    seriesCapped = series.length - keep.size;

    for (const g of groups.values()) {
      const rest = [];
      const restRows = [];
      for (const [name, vals] of [...g.buckets]) {
        if (keep.has(name)) continue;
        rest.push(...vals);
        restRows.push(...(g.rowsBy.get(name) ?? []));
        g.buckets.delete(name);
        g.rowsBy.delete(name);
      }
      if (rest.length) {
        g.buckets.set("Everything else", rest);
        g.rowsBy.set("Everything else", restRows);
      }
    }
    series = [...keep].sort();
    series.push("Everything else");
  }
  let data = [...groups.values()].map((g) => {
    const out = { group: g.label, _key: g.key, _total: 0, _rows: {}, _allRows: [] };
    for (const name of series) {
      const v = g.buckets.has(name) ? aggregate(g.buckets.get(name), measure.agg) : 0;
      out[name] = v;
      out._total += v;
      const rs = g.rowsBy.get(name) ?? [];
      out._rows[name] = rs;
      out._allRows.push(...rs);
    }
    return out;
  });

  /* A date axis reads in date order. Everything else reads biggest first,
     because the point of a category chart is which one is largest. */
  /* Blanks go last whichever way the rest is ordered. A "Not set" bar at the
     front of a trend is the first thing the eye lands on and the last thing
     anybody wants to talk about. */
  const blankLast = (a, b) =>
    (a._key === "__blank__") - (b._key === "__blank__");

  if (groupCol.type === "date" || spec.sort === "group") {
    data.sort((a, b) => blankLast(a, b) || String(a._key).localeCompare(String(b._key)));
  } else {
    data.sort((a, b) => blankLast(a, b) || b._total - a._total);
  }

  if (spec.limit && data.length > spec.limit) data = data.slice(0, spec.limit);

  return { kind, data, series, measureLabel, groupLabel: groupCol.label, seriesCapped };
}

/* A starting view for a tracker nobody has configured yet: count the rows by
   the first column that groups into a sensible number of buckets. Better than
   an empty panel, and it is a real chart rather than a placeholder. */
/* A column whose name says it holds a quantity worth adding up. Latitude is a
   number and summing it is meaningless; Amount is a number and summing it is
   the entire point. Guessing from the name is imperfect and beats guessing
   from the type alone. */
const MEASURE_NAMES = /^(value|amount|total|dollars?|cost|count|qty|quantity|hours|days|complete|remaining)$/i;
const looksLikeMeasure = (c) =>
  c.type === "number" && (MEASURE_NAMES.test(c.label.trim())
    || /\b(amount|dollars?|cost|total|value)\b/i.test(c.label));

export function suggestView(columns = [], rows = []) {
  /* A sheet turned round from months across the top is a trend, and the trend
     is what it was drawn to show. */
  const period = columns.find((c) => c.key === "period" && c.type === "date");
  const value = columns.find((c) => c.key === "value" && c.type === "number");
  if (period && value) {
    const measure = columns.find((c) => isCategory(c) && c.key !== "period_label");
    return {
      kind: "line", groupBy: period.key, bucket: "month",
      splitBy: measure?.key, measure: { agg: "sum", column: value.key },
    };
  }

  const money = columns.find(looksLikeMeasure);
  if (money) {
    const by = columns.find((c) => isCategory(c)
      && new Set(rows.map((r) => r[c.key])).size >= 2);
    if (by) {
      return { kind: "bar", groupBy: by.key, measure: { agg: "sum", column: money.key } };
    }
  }

  return suggestCountView(columns, rows);
}

function suggestCountView(columns = [], rows = []) {
  const candidates = columns.filter((c) => isCategory(c) || c.type === "date");
  let best = null;
  for (const c of candidates) {
    const distinct = new Set(rows.map((r) => r[c.key]).filter((v) => v !== null && v !== "")).size;
    if (distinct < 2) continue;
    /* The best first chart has a handful of bars, not two and not eighty.
       Four to eight distinct values is where a category chart says something;
       a date column beats all of them, because a trend always reads. */
    const score = c.type === "date" ? 20
      : distinct >= 3 && distinct <= 8 ? 15 - Math.abs(5 - distinct)
      : distinct <= 14 ? 8 - distinct
      : -distinct;
    if (!best || score > best.score) best = { column: c, score };
  }
  if (!best) return null;
  return {
    kind: best.column.type === "date" ? "column" : "bar",
    groupBy: best.column.key,
    measure: { agg: "count" },
    bucket: "month",
  };
}

export const describeView = (spec, columns) => {
  const g = byKey(columns, spec?.groupBy);
  const m = byKey(columns, spec?.measure?.column);
  const agg = AGGREGATIONS.find((a) => a.key === (spec?.measure?.agg ?? "count"));
  const what = spec?.measure?.agg === "count" || !m
    ? "Rows"
    : `${agg?.label} of ${m.label}`;
  if (spec?.kind === "kpi") return what;
  return g ? `${what} by ${g.label}` : what;
};

/* How wide a column wants to be, decided by what is in it.
 *
 * Every column used to share one rule, and the Status column paid for it: at
 * 260 pixels with breaks allowed anywhere, "Complete" wrapped as "Comple te",
 * which reads as a typo rather than as a narrow column. So a column of short
 * labels is sized to its longest label and never broken, and only genuine
 * prose is allowed to wrap.
 *
 * Length is the whole test, deliberately. Punctuation is not: "St. Croix" and
 * "Bldg. 4" are labels with full stops in them, and a column of island names
 * is exactly the kind of thing that must not wrap. Anything short enough to
 * sit on one line is left on one line, whatever is in it. */
export const LABEL_MAX = 22;

export function columnShapes(columns, rows) {
  const out = {};
  for (const c of columns) {
    /* A choice column holds one of a handful of short words by definition, so
       it never needs measuring. */
    if (!isCategory(c)) { out[c.key] = "label"; continue; }
    if (c.type === "choice") { out[c.key] = "label"; continue; }
    let shape = "label";
    for (const r of rows) {
      const v = r[c.key];
      if (v === null || v === undefined) continue;
      const str = String(v);
      if (str.length > LABEL_MAX) { shape = "prose"; break; }
    }
    out[c.key] = shape;
  }
  return out;
}

/* What is wrong with a set of columns, in the words somebody would use. */
export function columnProblems(columns = []) {
  const named = columns.filter((c) => c.label.trim());
  if (!named.length) return "Give at least one column a heading.";
  if (named.length !== columns.length) return "Every column needs a heading.";
  const seen = new Set();
  for (const c of named) {
    const k = c.label.trim().toLowerCase();
    if (seen.has(k)) return `Two columns are both called ${c.label.trim()}.`;
    seen.add(k);
  }
  const emptyChoice = named.find((c) => c.type === "choice" && !(c.options ?? []).length);
  if (emptyChoice) return `${emptyChoice.label.trim()} is a list of choices, so it needs some choices.`;
  return null;
}
